import { useSnackbarController } from "@firecms/core";
import { Alert, CircularProgress, Container, Paper, Skeleton, Typography } from "@firecms/ui";

import { buildPublicWebDownloadUrl } from "services/web-downloads/webDownloadsStorage";
import {
  COPY_LINK_FAILED_MESSAGE,
  COPY_LINK_SUCCESS_MESSAGE,
  DELETE_FAILED_MESSAGE,
  DELETE_FILE_SUCCESS_MESSAGE,
  DELETE_FOLDER_SUCCESS_MESSAGE,
  FOLDER_CREATED_MESSAGE,
  FOLDER_CREATE_FAILED_MESSAGE,
  UPLOAD_FAILED_MESSAGE,
  UPLOAD_SUCCESS_MESSAGE,
} from "./WebDownloadsManager.constants";
import { useWebDownloadsExplorer } from "./hooks/useWebDownloadsExplorer";
import ExplorerBreadcrumb from "./ui/ExplorerBreadcrumb";
import ExplorerEntryList from "./ui/ExplorerEntryList";
import ExplorerToolbar from "./ui/ExplorerToolbar";
import ExplorerUploadDropzone from "./ui/ExplorerUploadDropzone";
import WebDownloadsInfoAccordion from "./ui/WebDownloadsInfoAccordion";

const WebDownloadsManager: React.FC = () => {
  const snackbarController = useSnackbarController();
  const {
    currentPath,
    listing,
    loading,
    error,
    uploading,
    creatingFolder,
    deletingRelativePath,
    openFolder,
    navigateToPath,
    uploadFiles,
    createFolder,
    deleteFile,
    deleteFolder,
  } = useWebDownloadsExplorer();

  const handleUpload = async (files: File[]): Promise<void> => {
    try {
      await uploadFiles(files);
      snackbarController.open({
        type: "success",
        message: UPLOAD_SUCCESS_MESSAGE,
      });
    } catch {
      snackbarController.open({
        type: "error",
        message: UPLOAD_FAILED_MESSAGE,
      });
    }
  };

  if (loading && !listing) {
    return (
      <Container className="flex flex-col gap-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </Container>
    );
  }

  if (error && !listing) {
    return (
      <Container className="p-6">
        <Alert color="error">
          <Typography variant="h6">Fehler beim Laden</Typography>
          <Typography>{error.message}</Typography>
        </Alert>
      </Container>
    );
  }

  const entryCount = (listing?.folders.length ?? 0) + (listing?.files.length ?? 0);

  return (
    <Container className="flex max-w-5xl flex-col gap-4 p-6">
      <Typography variant="h4">Web-Downloads</Typography>

      <WebDownloadsInfoAccordion />

      <Paper className="overflow-hidden">
        <div className="border-b border-surface-200 px-4 py-3 dark:border-surface-700">
          <ExplorerBreadcrumb currentPath={currentPath} onNavigate={navigateToPath} />
        </div>

        <div className="flex flex-col gap-6 border-b border-surface-200 p-4 dark:border-surface-700">
          <ExplorerToolbar
            uploading={uploading}
            creatingFolder={creatingFolder}
            deleting={deletingRelativePath !== null}
            onCreateFolder={async (folderName) => {
              try {
                const createdName = await createFolder(folderName);
                if (!createdName) {
                  return false;
                }

                snackbarController.open({
                  type: "success",
                  message: FOLDER_CREATED_MESSAGE,
                });
                return true;
              } catch {
                snackbarController.open({
                  type: "error",
                  message: FOLDER_CREATE_FAILED_MESSAGE,
                });
                return false;
              }
            }}
          />

          <ExplorerUploadDropzone
            uploading={uploading}
            disabled={creatingFolder || deletingRelativePath !== null}
            onUploadFiles={handleUpload}
          />
        </div>

        <div className="p-4">
          {entryCount > 0 ? (
            <Typography variant="caption" color="secondary" className="mb-3 block">
              {entryCount} {entryCount === 1 ? "Eintrag" : "Einträge"}
            </Typography>
          ) : null}

          {loading ? (
            <div className="flex justify-center py-8">
              <CircularProgress />
            </div>
          ) : (
            <ExplorerEntryList
              folders={listing?.folders ?? []}
              files={listing?.files ?? []}
              deletingRelativePath={deletingRelativePath}
              actionsDisabled={uploading || creatingFolder}
              onOpenFolder={openFolder}
              onCopyLink={async (relativePath) => {
                try {
                  const publicUrl = buildPublicWebDownloadUrl(relativePath);
                  await navigator.clipboard.writeText(publicUrl);
                  snackbarController.open({
                    type: "success",
                    message: COPY_LINK_SUCCESS_MESSAGE,
                  });
                } catch {
                  snackbarController.open({
                    type: "error",
                    message: COPY_LINK_FAILED_MESSAGE,
                  });
                }
              }}
              onDeleteFile={async (relativePath) => {
                try {
                  await deleteFile(relativePath);
                  snackbarController.open({
                    type: "success",
                    message: DELETE_FILE_SUCCESS_MESSAGE,
                  });
                } catch {
                  snackbarController.open({
                    type: "error",
                    message: DELETE_FAILED_MESSAGE,
                  });
                }
              }}
              onDeleteFolder={async (relativePath) => {
                try {
                  await deleteFolder(relativePath);
                  snackbarController.open({
                    type: "success",
                    message: DELETE_FOLDER_SUCCESS_MESSAGE,
                  });
                } catch {
                  snackbarController.open({
                    type: "error",
                    message: DELETE_FAILED_MESSAGE,
                  });
                }
              }}
            />
          )}
        </div>
      </Paper>
    </Container>
  );
};

export default WebDownloadsManager;
